/**
 * @license
 * Copyright 2025 Google Inc.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import type { MultiscaleVolumeChunkSource } from "#src/sliceview/volume/frontend.js";
import { vec3 } from "#src/util/geom.js";
import type { VoxelPreviewMultiscaleSource } from "#src/voxel_annotation/PreviewMultiscaleChunkSource.js";
import type { RPC } from "#src/worker_rpc.js";

export const VOX_RELOAD_CHUNKS_RPC_ID = "vox.chunk.reload";
export const VOX_EDIT_BACKEND_RPC_ID = "vox.EditBackend";
export const VOX_EDIT_COMMIT_VOXELS_RPC_ID = "vox.edit.commitVoxels";
export const VOX_EDIT_FAILURE_RPC_ID = "vox.edit.failure";
export const VOX_EDIT_UNDO_RPC_ID = "vox.edit.undo";
export const VOX_EDIT_REDO_RPC_ID = "vox.edit.redo";
export const VOX_EDIT_HISTORY_UPDATE_RPC_ID = "vox.edit.historyUpdate";

export const VOX_EDIT_OPERATION_RPC_ID = "vox.edit.operation";

export const FLOODFILL_MAX_POSSIBLE_VOXELS = 1000000;
export const FLOODFILL_MIN_POSSIBLE_VOXELS = 1000;

export enum VoxelOperationType {
  BRUSH = 0,
  FLOOD_FILL = 1,
}

export interface VoxelOperationBase {
  type: VoxelOperationType;
  // Frontend dispatch sequence number. Echoed back per written chunk (as the
  // max seq the write covers) in reload messages, so the frontend clears an
  // overlay chunk only once the stored data covers every dispatched stroke
  // that touched it.
  seq?: number;
}

export interface BrushOperation extends VoxelOperationBase {
  type: VoxelOperationType.BRUSH;
  centers: Float32Array[];
  radius: number;
  value: bigint;
  shape: BrushShape;
  basis: { u: Float32Array; v: Float32Array };
  filterValue?: bigint;
}

export interface FloodFillOperation extends VoxelOperationBase {
  type: VoxelOperationType.FLOOD_FILL;
  seed: Float32Array;
  value: bigint;
  maxVoxels: number;
  basis: { u: Float32Array; v: Float32Array };
  filterValue?: bigint;
}

export type VoxelOperation = BrushOperation | FloodFillOperation;

export const BRUSH_TOOL_ID = "vox-brush";
export const FLOODFILL_TOOL_ID = "vox-flood-fill";
export const VALUE_PICKER_TOOL_ID = "vox-value-picker";

export const VOXEL_EMPTY_VALUE = 0n;

// Special value used to indicate to the optimistic renderer that a voxel has been erased
export const SEG_ERASE_SENTINEL = ~1n;

export const VOXEL_EDIT_STAMINA = {
  pendingEdits: (voxelCount: number) => Math.round(voxelCount * 0.0004),
  downsamplingJobs: (count: number, downsamplingSteps: number) =>
    Math.round(count * 10 * downsamplingSteps),
  brush: (shape: BrushShape, radius: number, hasFiltering: boolean) => {
    const FILTERING = hasFiltering ? 7 : 1;
    if (shape === BrushShape.DISK) {
      return Math.round(0.00035 * Math.pow(radius, 2) * FILTERING);
    } else {
      return Math.round(0.00012 * Math.pow(radius, 3) * FILTERING);
    }
  },
  floodFill: (maxVoxels: number) => Math.round(maxVoxels * 0.005),
  undoRedo: () => 20,
};
export const MAX_VOXEL_EDIT_STAMINA = 10000;

export type VoxelValueGetter = (isPreview: boolean) => bigint;

export interface VoxelLayerResolution {
  lodIndex: number;
  transform: number[];
  chunkSize: number[];
  sourceRpc: number;
}

export type VoxelChangeValues = Uint32Array | BigUint64Array;

export interface VoxelChange {
  indices: Uint32Array;
  oldValues: VoxelChangeValues;
  newValues: VoxelChangeValues;
}

export interface EditAction {
  changes: Map<string, VoxelChange>;
  timestamp: number;
  description: string;
}

export function makeVoxChunkKey(chunkKey: string, lodIndex: number) {
  return `lod${lodIndex}#${chunkKey}`;
}

export function makeChunkKey(x: number, y: number, z: number) {
  return `${x},${y},${z}`;
}

export function parseVoxChunkKey(key: string) {
  const parts = [
    Number(key.split("#")[0].substring(3)),
    ...key.split("#")[1].split(",").map(Number),
  ];
  if (parts.length !== 4 || parts.some(isNaN)) {
    console.warn(`Invalid chunk key format: ${key}`);
    return null;
  }
  return {
    lodIndex: parts[0],
    x: parts[1],
    y: parts[2],
    z: parts[3],
    chunkKey: key.split("#")[1],
  };
}

export function getBasisFromNormal(n: vec3) {
  const u = vec3.create();
  const tempVec =
    Math.abs(vec3.dot(n, vec3.fromValues(1, 0, 0))) < 0.9
      ? vec3.fromValues(1, 0, 0)
      : vec3.fromValues(0, 1, 0);
  vec3.cross(u, tempVec, n);
  vec3.normalize(u, u);
  const v = vec3.cross(vec3.create(), n, u);
  vec3.normalize(v, v);
  return { u, v };
}

export enum BrushShape {
  DISK = 0,
  SPHERE = 1,
}

const sphereRowRangesKernelCache = new Map<number, Int16Array>();

export function getSphereRowRangesKernel(radius: number): Int16Array {
  let kernel = sphereRowRangesKernelCache.get(radius);
  if (kernel !== undefined) return kernel;
  if (!Number.isInteger(radius) || radius < 0) {
    throw new Error(`Invalid sphere radius: ${radius}`);
  }
  const rr = radius * radius;
  const ranges: number[] = [];
  for (let dz = -radius; dz <= radius; ++dz) {
    const dz2 = dz * dz;
    for (let dy = -radius; dy <= radius; ++dy) {
      const remaining = rr - dz2 - dy * dy;
      if (remaining < 0) continue;
      const maxDx = Math.floor(Math.sqrt(remaining));
      ranges.push(dy, dz, -maxDx, maxDx + 1);
    }
  }
  kernel = Int16Array.from(ranges);
  sphereRowRangesKernelCache.set(radius, kernel);
  return kernel;
}

const diskStencilKernelCache = new Map<number, Int16Array>();

export function getDiskStencilKernel(radius: number): Int16Array {
  let kernel = diskStencilKernelCache.get(radius);
  if (kernel !== undefined) return kernel;
  if (!Number.isInteger(radius) || radius < 0) {
    throw new Error(`Invalid disk radius: ${radius}`);
  }
  const rr = radius * radius;
  const pairs: number[] = [];
  for (let j = -radius; j <= radius; ++j) {
    for (let i = -radius; i <= radius; ++i) {
      if (i * i + j * j <= rr) {
        pairs.push(i, j);
      }
    }
  }
  kernel = Int16Array.from(pairs);
  diskStencilKernelCache.set(radius, kernel);
  return kernel;
}

export interface VoxelEditControllerHost {
  primarySource: MultiscaleVolumeChunkSource;
  previewSource?: VoxelPreviewMultiscaleSource;
  rpc: RPC;
}
